import type { Framework } from '@toeverything/infra';

import { getFrameSnapshotService } from './services/frame-snapshot';

export { getFrameSnapshotService } from './services/frame-snapshot';
export type { FrameSnapshotProvider, SnapshotEntry } from './types';

/**
 * Frame Snapshot Hook Interface
 *
 * Registered as window.__streamlainFrameSnapshotHook
 * Called from BlockSuite frame-preview.ts (minimal hook pattern)
 */
interface FrameSnapshotHook {
  getSnapshot: (frameId: string) => string | null;
  capture: (
    frameId: string,
    element: Element,
    docId?: string,
    backgroundColor?: string
  ) => void;
  invalidate: (frameId: string) => void;
}

/** Track frames currently being loaded from IndexedDB */
const pendingLoads = new Set<string>();

/**
 * Configure Frame Thumbnail Module
 *
 * Strategy: Hook Pattern (BLOCKSUITE_EXTENSION_GUIDE.md)
 * - Minimal changes to BlockSuite (~17 lines in frame-preview.ts)
 * - Full logic in this proprietary module
 *
 * Storage: Isolated IndexedDB (no cloud sync!)
 * - Database: 'streamlain-frame-snapshots'
 * - TTL: 30 days
 * - Max entries: 200
 */
export function configureFrameThumbnailModule(_framework: Framework) {
  const service = getFrameSnapshotService();

  // Register hook for BlockSuite integration (hook pattern)
  if (typeof window !== 'undefined') {
    const hook: FrameSnapshotHook = {
      getSnapshot: frameId => {
        // 1. Check memory cache (sync)
        const cached = service.getSnapshot(frameId);
        if (cached) return cached;

        // 2. If not in memory and not already loading, try IndexedDB (async)
        if (!pendingLoads.has(frameId)) {
          pendingLoads.add(frameId);
          service
            .loadFromDB(frameId)
            .then(html => {
              pendingLoads.delete(frameId);
              if (html) {
                // Dispatch event to trigger re-render in frame-preview
                window.dispatchEvent(
                  new CustomEvent('streamlain-frame-snapshot-loaded', {
                    detail: { frameId },
                  })
                );
                console.log(
                  `[FrameThumbnail] Loaded from IndexedDB, dispatched event: ${frameId}`
                );
              }
            })
            .catch(err => {
              pendingLoads.delete(frameId);
              console.warn(
                `[FrameThumbnail] Failed to load from IndexedDB: ${frameId}`,
                err
              );
            });
        }

        return null; // Return null for now, re-render will happen via event
      },
      capture: (frameId, element, docId, backgroundColor) => {
        // Skip if already in memory cache
        if (service.getSnapshot(frameId)) return;

        // Use requestIdleCallback to avoid blocking
        requestIdleCallback(
          () => {
            // Double-check after idle (might have been loaded from IndexedDB)
            if (service.getSnapshot(frameId)) return;
            service
              .capture(frameId, element, docId, backgroundColor)
              .catch(err => {
                console.warn(
                  `[FrameThumbnail] Capture failed: ${frameId}`,
                  err
                );
              });
          },
          { timeout: 3000 }
        );
      },
      invalidate: frameId => service.invalidate(frameId),
    };

    (window as any).__streamlainFrameSnapshotHook = hook;

    // Also expose service for debugging
    (window as any).__frameSnapshotService = service;
  }
}
